import "./case-table.scss";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CaseStatus } from "../case-status/case-status";
import { SearchBar, SearchBarHandle } from "../search-bar/search-bar";
import { Pagination } from "../pagination/pagination";
import { useNavigate } from "react-router-dom";
import { useGetSolicitorCaseQuery } from "../../../../redux/services/getSolicitor";
import { useAppDispatch, useAppSelector } from "../../../../redux/hook";
import { P2Alert } from "react-bootstrap-common-script";
import { FilterTag } from "../filter-tag/filter-tag";
import { HDBLinks } from "../../../../utils/shared/links";
import { formatHdbRef } from "../../../../utils/string-formatter";
import { setApplicationId } from "../../../../redux/slices/application-id-slice";
import { Helpers } from "../../../../utils/helper";
import ExclamationMark from "../../../../assets/svg/exclamation-mark";
import { SolicitorNotificationStatusEnum } from "../../../../models/enum/solicitor-notification-status.enum";
import { LottieLoader } from "../../../loader";
import { SolicitorNotificationTagEnum } from "../../../../models/enum/solicitor-notification-tag.enum";
import useIsMobile from "../../../../hooks/use-is-mobile";

export function CaseTable() {
  // States and data fetching
  const loginUser = useAppSelector((state) => state.auth.AuthenticationSlice.loginUser);
  const singpassToken = useAppSelector((state) => state.auth.AuthenticationSlice.singpassToken);
  const corpPassDetails = useAppSelector((state) => state.auth.AuthenticationSlice.corpPassDetails);
  const AppDispatch = useAppDispatch();

  const { isMobile } = useIsMobile();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [tag, setTag] = useState<SolicitorNotificationTagEnum | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(0); // Uses 0 index

  const { data, isError, isFetching, refetch } = useGetSolicitorCaseQuery(
    {
      uinfin: loginUser?.uinfin ?? "",
      singpass_token: singpassToken ?? "",
      uen: corpPassDetails.uen,
      search_term: searchTerm,
      tag: tag,
      page: currentPage,
    },
    { skip: !loginUser || !singpassToken }
  );

  const navigate = useNavigate();

  // Refs
  const searchBarRef = useRef<SearchBarHandle>(null);
  const tableRef = useRef<HTMLTableElement>(null);

  const hasTag = !!tag;
  const hasFilter = searchTerm !== "" || hasTag;
  const showNoNewCase = !isFetching && !isError && hasTag && data?.empty;
  const showNoCases = !isFetching && !isError && !hasTag && data?.empty;
  const showTable = !isFetching && !isError && !showNoNewCase && !showNoCases;

  // Functions
  const onSearchCallback = useCallback(() => {
    if (searchBarRef.current === null) {
      setSearchTerm("");
      return;
    }

    setSearchTerm(searchBarRef.current.getSearchTerm());
    setCurrentPage(0);
  }, []);

  function createNavigateToCaseDetails(application_uuid: string, hdb_ref: string) {
    return () => {
      navigate(`/solicitor/case?applicationUuid=${application_uuid}&hdbRef=${hdb_ref}`);
      AppDispatch(setApplicationId(application_uuid));
    };
  }

  function handlePageSelectCallback() {
    if (!tableRef.current) return;

    if (tableRef.current.getBoundingClientRect().top > 0) return;

    tableRef.current?.scrollIntoView({ behavior: "auto" });
  }

  useEffect(() => {
    refetch();
  }, [searchTerm, hasTag, currentPage]);

  return (
    <>
      <SearchBar
        ref={searchBarRef}
        isFetching={isFetching}
        isError={isError}
        latestSearchTerm={searchTerm}
        numberOfResults={data?.number_of_elements ?? 0}
        onSearchCallback={onSearchCallback}
        tag={tag}
      >
        <FilterTag disabled={isError || (!hasFilter && data?.empty)} tag={tag} setTag={setTag} />
      </SearchBar>

      {showNoNewCase ? (
        <P2Alert
          variant="success"
          title="You have no new updates currently."
          text="Select the 'All cases' button to view all ongoing cases."
          withBorder
          style={{ marginBottom: 80 }}
        />
      ) : null}

      {showNoCases ? (
        <P2Alert
          variant="warning"
          title="You have no outstanding cases currently."
          text={
            <>
              <span>{"Otherwise, please write to us."}</span>
            </>
          }
          withBorder
        />
      ) : null}

      {isError ? (
        <P2Alert
          variant="danger"
          title="We are unable to retrieve your cases currently."
          text={
            <>
              <span>{"Please refresh the page or try again later. If you are still unable to view your cases, please write to us."}</span>
            </>
          }
          withBorder
        />
      ) : null}

      {isFetching ? <LottieLoader /> : null}

      {showTable && !isMobile ? (
        <>
          <table ref={tableRef} className="polymer-case-table__container">
            <tr className="polymer-case-table__header">
              <th className="polymer-case-table__header-bullet-point" />
              <th className="polymer-case-table__header-hdb-ref">HDB Reference No.</th>
              <th className="polymer-case-table__header-address">Address</th>
              <th className="polymer-case-table__header-status">Status</th>
              <th className="polymer-case-table__header-date">Completion Date</th>
            </tr>
            <tbody>
              {data?.content.map((caseItem, _index) => {
                const isHighlighted = caseItem.tag !== SolicitorNotificationTagEnum.no_update;

                return (
                  <tr
                    key={`${_index}_${caseItem.application_uuid}`}
                    className={`polymer-case-table__row ${isHighlighted ? "active" : ""}`}
                    onClick={createNavigateToCaseDetails(caseItem.application_uuid, caseItem.hdb_ref)}
                  >
                    <td className="polymer-case-table__row-bullet-point">{isHighlighted ? <div className="circle" /> : null}</td>
                    <td className="polymer-case-table__row-hdb-ref">{formatHdbRef(caseItem.hdb_ref)}</td>
                    <td className="polymer-case-table__row-address">
                      <div className="polymer-case-table__row-address-content">
                        {caseItem.notification_status === SolicitorNotificationStatusEnum.removed ? <ExclamationMark size={24} fillColor="#CB2A0C" /> : null}
                        {caseItem.address}
                      </div>
                    </td>
                    <td className="polymer-case-table__row-status">
                      <CaseStatus status={caseItem.status} lodgementDate={caseItem.lodgement_date} />
                    </td>
                    <td className="polymer-case-table__row-date">{Helpers.formatDate(caseItem.completion_date, "short", false)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      ) : null}

      {showTable && isMobile ? (
        <div style={{ marginBottom: 24 }}>
          {data?.content.map((caseItem, _index) => {
            const isHighlighted = caseItem.tag !== SolicitorNotificationTagEnum.no_update;

            return (
              <div
                key={`${_index}_${caseItem.application_uuid}`}
                className={`polymer-case-list__row ${isHighlighted ? "active" : ""}`}
                onClick={createNavigateToCaseDetails(caseItem.application_uuid, caseItem.hdb_ref)}
              >
                <CaseStatus status={caseItem.status} lodgementDate={caseItem.lodgement_date} style={{ marginBottom: 8 }} />
                <div className="polymer-case-list__ref-date-container">
                  <span>
                    {isHighlighted ? <div className="circle" /> : null}
                    {formatHdbRef(caseItem.hdb_ref)}
                  </span>
                  <span>{Helpers.formatDate(caseItem.completion_date, "short", false)}</span>
                </div>
                <span className="polymer-case-list__address">{caseItem.address}</span>
              </div>
            );
          })}
        </div>
      ) : null}

      {showTable ? (
        <Pagination
          currentPage={currentPage}
          pageable={data}
          setCurrentPage={setCurrentPage}
          callback={handlePageSelectCallback}
          style={{ marginBottom: 24 }}
        />
      ) : null}
    </>
  );
}
